// Line-item health: decides when a row/tile is CRITICAL (needs action now) or STUCK
// (nothing has moved for too long). Pure functions over the Firestore doc, so every
// section uses the same rules. Returns { level: "critical" | "stuck", reason } or null.
import { toDate } from "@/lib/format";

const H = 3600e3;
const D = 24 * H;
const ms = (v) => toDate(v)?.getTime() || 0;
const lower = (v) => String(v ?? "").toLowerCase();
const age = (...vals) => {
  const t = Math.max(...vals.map(ms));
  return t ? Date.now() - t : 0;
};
const lastTouch = (d) => Math.max(ms(d.updated_at), ms(d.status_changed_at), ms(d.created_at), ms(d.server_ts));
const idle = (d) => (lastTouch(d) ? Date.now() - lastTouch(d) : 0);
const crit = (reason) => ({ level: "critical", reason });
const stuck = (reason) => ({ level: "stuck", reason });
const days = (n) => `${Math.max(1, Math.round(n / D))}d`;
const hrs = (n) => (n >= 2 * D ? days(n) : `${Math.max(1, Math.round(n / H))}h`);

const MS_DONE = ["customer_approved", "completed", "approved", "paid", "done", "verified", "cancelled"];
const lastMsgFrom = (d) => (Array.isArray(d.messages) && d.messages.length ? d.messages[d.messages.length - 1].from : null);

export const RULES = {
  project_milestones: (m) => {
    const st = lower(m.status);
    if (st === "disputed") return crit("Disputed by customer");
    const pay = lower(m.payment_status);
    if (!["paid", "waived"].includes(pay) && m.payment_due_at) {
      const dl = ms(m.payment_deadline_at) || ms(m.payment_due_at) + 48 * H;
      if (Date.now() > dl) return crit(`Advance payment overdue ${days(Date.now() - dl)}`);
      if (dl - Date.now() < 12 * H) return stuck(`Payment due in ${hrs(dl - Date.now())}`);
    }
    if (MS_DONE.includes(st)) return null;
    const end = ms(m.expected_end_date);
    if (end && Date.now() > end + 12 * H) return crit(`Overdue by ${days(Date.now() - end)}`);
    if (m.started_late && st === "in_progress") return crit("Started late");
    const start = ms(m.expected_start_date);
    if (start && Date.now() > start + D && !m.actual_start_at && ["locked", "ready_to_pay", "pending", "not_started", ""].includes(st)) return stuck(`Should have started ${days(Date.now() - start)} ago`);
    if (m.ready_for_review_at && ["under_review", "ready_for_review"].includes(st) && age(m.ready_for_review_at) > 48 * H) return stuck(`Awaiting customer approval ${hrs(age(m.ready_for_review_at))}`);
    if (lower(m.payment_status) === "link_sent" && idle(m) > 72 * H) return stuck(`Payment link unpaid ${hrs(idle(m))}`);
    return null;
  },
  milestone_plans: (p) => {
    const st = lower(p.status);
    if (st === "change_requested") return crit("Customer asked for changes");
    if (st === "declined") return crit("Customer declined the plan");
    const waitFor = { sent_to_customer: "customer review", pending_customer_signature: "customer signature", pending_pro_signature: "pro signature", pending_brugo_signature: "Brugo signature", accepted: "signatures" }[st];
    if (waitFor && idle(p) > 48 * H) return stuck(`Waiting on ${waitFor} ${hrs(idle(p))}`);
    if (st === "pending_brugo_signature") return crit("Brugo needs to sign");
    return null;
  },
  supervisor_visits: (v) => {
    const sev = lower(v.severity);
    if (["critical", "high", "major"].includes(sev) && lower(v.cc_status) !== "resolved") return crit(v.remark ? `Concern: ${String(v.remark).slice(0, 60)}` : "Critical site concern");
    if (v.geo && v.geo.within_geofence === false) return stuck("Logged outside the site geofence");
    return null;
  },
  critical_notifications: (n) => (lower(n.status) === "unread" || lower(n.status) === "open" ? crit(n.title || "Supervisor concern") : null),
  promise_requests: (p) => {
    const st = lower(p.status || "submitted");
    if (p.payment_mismatch) return crit("Paid amount below the fee");
    if (["submitted", "interested", "pending", "requested", "new"].includes(st) && idle(p) > 24 * H) return stuck(`Not reviewed for ${hrs(idle(p))}`);
    if (st === "in_review" && idle(p) > 48 * H) return stuck(`In review for ${hrs(idle(p))}`);
    if (st === "payment_pending" && !p.fee) return crit("Payment pending but no fee set");
    if (st === "payment_pending" && idle(p) > 72 * H) return stuck(`Unpaid for ${hrs(idle(p))}`);
    return null;
  },
  support_conversations: (t) => ticketHealth(t),
  support_tickets: (t) => ticketHealth(t),
  // Only recent leads raise alarms; anything older is history, not something to act on now.
  requests: (r) => {
    const st = lower(r.customer_status || r.internal_status);
    if (["hired", "closed", "cancelled", "completed", "expired"].includes(st)) return null;
    const born = Math.max(ms(r.created_at), ms(r.general_phase_started_at));
    const ageMs = born ? Date.now() - born : 0;
    if (r.internal_status === "ops_required") return ageMs < 3 * D ? crit("Ops required — no pro picked it up") : ageMs < 14 * D ? stuck(`Ops required for ${days(ageMs)}`) : null;
    const stats = r.lead_distribution_stats || {};
    const ends = ms(r.general_phase_ends_at);
    const noReply = ends && Date.now() > ends && !(Number(stats.responded) > 0) && !(Number(r.response_count) > 0);
    if (noReply && Date.now() - ends < 2 * D) return crit("Window closed with no responses");
    if (noReply && Date.now() - ends < 7 * D) return stuck(`No responses (${days(Date.now() - ends)} ago)`);
    if (ageMs < 14 * D && idle(r) > 3 * D) return stuck(`No movement for ${days(idle(r))}`);
    return null;
  },
  projects: (p) => {
    const st = lower(p.status);
    if (["delayed", "off_track"].includes(st)) return crit(st === "delayed" ? "Project delayed" : "Off track");
    if (st === "at_risk") return crit("At risk");
    if (["completed", "cancelled", "closed"].includes(st)) return null;
    if (st === "active" && idle(p) > 7 * D) return stuck(`No update for ${days(idle(p))}`);
    return null;
  },
  orders: (o) => {
    const st = lower(o.current_status || o.status).replace(/ /g, "_");
    if (o.return_request || o.cancellation) return crit("Cancellation / return requested");
    if (["delivered", "cancelled", "returned"].includes(st)) return null;
    if (age(o.created_at, o.order_placed_at) > 48 * H && ["order_placed", "confirmed", "packed", ""].includes(st)) return stuck(`Not shipped after ${hrs(age(o.created_at, o.order_placed_at))}`);
    return null;
  },
  professionals: (p) => (p.onboarding_status === "submitted" && idle(p) > 48 * H ? stuck(`Approval pending ${hrs(idle(p))}`) : null),
  razorpay_payments: (p) => (lower(p.status) === "failed" ? crit("Payment failed") : null),
  disputes: (d) => (["resolved", "closed"].includes(lower(d.status)) ? null : crit("Open dispute")),
};

function ticketHealth(t) {
  const st = lower(t.status || "open");
  if (["resolved", "closed"].includes(st)) return null;
  const waiting = lastMsgFrom(t) !== "brugo_support";
  if (lower(t.priority) === "urgent") return crit("Urgent ticket");
  if (waiting && idle(t) > 24 * H) return stuck(`Awaiting our reply ${hrs(idle(t))}`);
  if (lower(t.priority) === "high" && waiting) return crit("High priority, awaiting reply");
  return null;
}

export function healthOf(coll, doc) {
  const fn = RULES[String(coll).split("/")[0]];
  if (!fn || !doc) return null;
  try { return fn(doc); } catch { return null; }
}

// Worst health across many docs (for tiles / tabs / projects with milestones).
export function worstOf(list) {
  const items = list.filter(Boolean);
  return items.find((h) => h.level === "critical") || items.find((h) => h.level === "stuck") || null;
}

export const healthRowClass = (h) => (h ? (h.level === "critical" ? "cc-critical" : "cc-stuck") : "");
